import React, { useState } from "react";

import StandardButton from "../../../../../../components/Buttons/StandardButton";
import LoadingIcon from "../../../../../../components/Loaders/LoadingIcon";
import OverCreditBanner from "../../../../../../components/Paywall/OverCreditBanner";
import CheckIcon from "../../../../../../resources/route-builder/check.svg";
import SparkAiIcon from "../../../../../../resources/sparkai.svg";
import { maxAllowedRouteCount } from "../../lib/helpers";
import {
  expandRouteNamePrefix,
  suggestRouteNamePrefix,
} from "../../lib/serviceHelpers";
import { useRouteBuilder } from "../../context/RouteBuilderContext";
import PropertyFiltersStep from "./PropertyFiltersStep";
import RouteSetupStep from "./RouteSetupStep";

const WIZARD_STEPS = [
  { step: 1 as const, label: "Property filters" },
  { step: 2 as const, label: "Route setup" },
];

type WizardStep = 1 | 2;

export default function SetupForm() {
  const {
    inputs,
    session,
    routeNameOverride,
    setRouteNameOverride,
    selectedPropertyCount,
    generate,
    isBusy,
    isLoadingResources,
    isRefreshingProperties,
    optimizationBlocked,
    optimizationCreditsWarning,
  } = useRouteBuilder();
  // Existing plans open directly to their route settings.
  const [wizardStep, setWizardStep] = useState<WizardStep>(session ? 2 : 1);
  const routeNamePrefix = routeNameOverride ?? suggestRouteNamePrefix(inputs);

  const maxRouteCount = maxAllowedRouteCount(selectedPropertyCount);
  const routeCount = Math.min(inputs.routeCount, maxRouteCount);

  const goNext = () => setWizardStep(2);
  const goBack = () => setWizardStep(1);

  return (
    <>
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-6 pr-12 lg:px-10 lg:pr-12 [scrollbar-gutter:stable]">
        <div>
          <h2
            id="route-builder-setup-title"
            className="text-xl font-semibold text-snow-primary"
          >
            Route Builder configuration
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Filter properties, set up routes, then generate.
          </p>
        </div>

        {optimizationBlocked || optimizationCreditsWarning ? (
          <OverCreditBanner
            message={
              optimizationBlocked
                ? "You've used all route optimization credits for this month."
                : optimizationCreditsWarning
            }
            linkTo="/admin/settings/billing?show-plans=1"
            linkText={
              optimizationBlocked
                ? "Upgrade your plan to optimize more routes."
                : "Upgrade now"
            }
          />
        ) : null}

        <nav aria-label="Setup steps" data-testid="setup-wizard-steps">
          <ol className="flex w-full items-center">
            {WIZARD_STEPS.map((item, index) => {
              const isCurrent = wizardStep === item.step;
              const isDone = wizardStep > item.step;
              const isLast = index === WIZARD_STEPS.length - 1;
              return (
                <React.Fragment key={item.step}>
                  <li
                    data-testid={`wizard-step-${item.step}`}
                    aria-current={isCurrent ? "step" : undefined}
                    className="flex shrink-0 items-center gap-3"
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                        isDone
                          ? "bg-snow-primary text-white"
                          : isCurrent
                            ? "bg-snow-primary text-white ring-4 ring-sky-100"
                            : "border-2 border-slate-300 bg-white text-slate-500"
                      }`}
                    >
                      {isDone ? (
                        <img
                          src={CheckIcon}
                          alt=""
                          className="h-4 w-4"
                          aria-hidden
                        />
                      ) : (
                        item.step
                      )}
                    </span>
                    <span
                      className={`text-sm font-semibold ${
                        isCurrent || isDone
                          ? "text-snow-primary"
                          : "text-slate-500"
                      }`}
                    >
                      {item.label}
                    </span>
                  </li>
                  {!isLast ? (
                    <li
                      className="mx-4 h-0.5 min-w-8 flex-1 list-none"
                      aria-hidden
                    >
                      <div
                        className={`h-full w-full ${
                          isDone ? "bg-snow-primary" : "bg-slate-200"
                        }`}
                      />
                    </li>
                  ) : null}
                </React.Fragment>
              );
            })}
          </ol>
        </nav>

        <div className="space-y-5">
          {isLoadingResources ? (
            <div className="relative flex min-h-[18rem] items-center justify-center">
              <LoadingIcon fixed={false} size={48} />
            </div>
          ) : null}

          {!isLoadingResources && wizardStep === 1 ? (
            <PropertyFiltersStep />
          ) : null}

          {!isLoadingResources && wizardStep === 2 ? (
            <RouteSetupStep
              maxRouteCount={maxRouteCount}
              routeCount={routeCount}
              routeNamePrefix={routeNamePrefix}
              onRouteNameChange={setRouteNameOverride}
            />
          ) : null}
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-white px-4 py-4 pr-12 lg:px-10 lg:pr-12">
        {wizardStep > 1 ? (
          <button
            type="button"
            data-testid="wizard-back-button"
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={goBack}
          >
            Back
          </button>
        ) : null}
        {wizardStep < 2 ? (
          <StandardButton
            color="blue"
            label="Continue"
            disabled={
              selectedPropertyCount === 0 || isBusy || isRefreshingProperties
            }
            onClick={goNext}
            testId="wizard-continue-button"
            className="!px-3 !py-2"
          />
        ) : (
          <StandardButton
            color="blue"
            disabled={
              selectedPropertyCount === 0 ||
              isBusy ||
              isRefreshingProperties ||
              !inputs.plannedDispatchTime ||
              (inputs.routeType === "temporary" && !inputs.expiresAt)
            }
            loading={isBusy}
            onClick={() =>
              generate(expandRouteNamePrefix(routeNamePrefix, routeCount))
            }
            testId="generate-routes-button"
            className="!flex !h-10 !w-40 !items-center !justify-center !px-3 !py-2"
          >
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <img src={SparkAiIcon} alt="" className="size-4" />
              Generate routes
            </span>
          </StandardButton>
        )}
      </div>
    </>
  );
}
